import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { FeatureMaturity, NetworkRestriction, UserSettings } from '@/types/settings'

interface UserState {
  // User account
  userId: string | null
  email: string | null
  createdAt: Date | null
  
  // Settings
  settings: UserSettings
  
  // Actions
  setUser: (userId: string, email: string) => void
  updateSettings: (settings: Partial<UserSettings>) => void
  clearUser: () => void
}

const defaultSettings: UserSettings = {
  // Feature maturity preferences
  featureAccess: {
    alpha: false,
    beta: false,
    stable: true
  },
  
  // Network restrictions by feature level
  networkRestrictions: {
    alpha: 'testnet-only',
    beta: 'testnet-default',
    stable: 'all-networks'
  },
  
  // Transaction limits (in satoshis)
  transactionLimits: {
    alpha: 0, // No mainnet transactions
    beta: 100000, // 0.001 BTC (~$50 at $50k/BTC)
    stable: null // No limit
  },
  
  // Address type preferences
  addressPreferences: {
    preferQuantum: true,
    allowClassical: true,
    defaultDerivationPath: "m/3077'/0'/0'/0/0"
  },
  
  // Security settings
  security: {
    requireBiometric: false,
    autoLockMinutes: 5,
    requirePasswordForSend: true,
    maxDailySpend: 1000000 // 0.01 BTC
  },
  
  // Developer settings
  developer: {
    showTechnicalDetails: false,
    enableLogging: false,
    showTestnetWarnings: true
  }
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      userId: null,
      email: null,
      createdAt: null,
      settings: defaultSettings,
      
      setUser: (userId: string, email: string) => {
        set({
          userId,
          email,
          createdAt: new Date()
        })
      },
      
      updateSettings: (newSettings: Partial<UserSettings>) => {
        set((state) => ({
          settings: {
            ...state.settings,
            ...newSettings,
            // Deep merge for nested objects
            featureAccess: {
              ...state.settings.featureAccess,
              ...(newSettings.featureAccess || {})
            },
            networkRestrictions: {
              ...state.settings.networkRestrictions,
              ...(newSettings.networkRestrictions || {})
            },
            transactionLimits: {
              ...state.settings.transactionLimits,
              ...(newSettings.transactionLimits || {})
            },
            addressPreferences: {
              ...state.settings.addressPreferences,
              ...(newSettings.addressPreferences || {})
            },
            security: {
              ...state.settings.security,
              ...(newSettings.security || {})
            },
            developer: {
              ...state.settings.developer,
              ...(newSettings.developer || {})
            }
          }
        }))
      },
      
      clearUser: () => {
        set({
          userId: null,
          email: null,
          createdAt: null,
          settings: defaultSettings
        })
      }
    }),
    {
      name: 'qkey-user-storage',
      partialize: (state) => ({
        userId: state.userId,
        email: state.email,
        createdAt: state.createdAt,
        settings: state.settings
      })
    }
  )
)